import * as v from "valibot";
import type {CSSProperties, ReactNode, ComponentProps} from "react";
import {useEffect, useState} from "react";
import {Col, DSButton, Text} from "@cdx/ds";
import {useDelayedTrigger} from "../delayed-trigger";
import {errorToString, getFieldErrors} from "../error-utils";
import type {ValibotFormArgs} from "./lib/create-valibot-neo-form";
import {useValibotNeoForm} from "./lib/create-valibot-neo-form";
import type {FormDataSlice} from "./lib/utils";
import {useBuildFormComponent} from "./lib/utils";

v.setSpecificMessage(v.nonEmpty, () => `Required`);

type FormSubmitState = "initial" | "loading" | "success" | "error";

type MyFormProps = {
  buttonLabel: string | null;
  stickyButton?: boolean;
  formStateSlice: FormDataSlice<FormSubmitState>;
  serverErrorSlice: FormDataSlice<string | null>;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  forwardProps?: Omit<ComponentProps<"form">, "className" | "onSubmit" | "style" | "children">;
};

const FormWrapper = ({
  buttonLabel = "Save",
  stickyButton,
  formStateSlice,
  serverErrorSlice,
  children,
  forwardProps,
  ...rest
}: MyFormProps) => {
  const state = formStateSlice.useValue((s) => s);
  const serverError = serverErrorSlice.useValue();

  return (
    <form {...forwardProps} {...rest}>
      {serverError && (
        <Text type="label12" color="error" pb="12px">
          {serverError}
        </Text>
      )}
      {children}
      {buttonLabel &&
        (stickyButton ? (
          <Col align="end" px="32px" py="12px" bg position="sticky" bottom="0" border="1pxTopLight">
            <DSButton type="submit" state={state}>
              {buttonLabel}
            </DSButton>
          </Col>
        ) : (
          <Col align="start">
            <DSButton type="submit" state={state}>
              {buttonLabel}
            </DSButton>
          </Col>
        ))}
    </form>
  );
};

const handleSubmitPromise = (
  res: Promise<unknown>,
  deps: {
    trigger: ReturnType<typeof useDelayedTrigger>;
    formStateSlice: FormDataSlice<FormSubmitState>;
    serverErrorSlice: FormDataSlice<string | null>;
    serverErrorFieldSlice: FormDataSlice<Record<string, string>>;
    fieldNames: Set<string>;
  }
) => {
  const {trigger, formStateSlice, serverErrorSlice, serverErrorFieldSlice, fieldNames} = deps;
  formStateSlice.setValue("loading");
  serverErrorSlice.setValue(null);
  serverErrorFieldSlice.setValue({});
  trigger.cancel();
  res.then(
    () => {
      formStateSlice.setValue("success");
      trigger.fire(() => formStateSlice.setValue("initial"), 2000);
    },
    (err) => {
      formStateSlice.setValue("error");
      trigger.fire(() => formStateSlice.setValue("initial"), 2000);
      const serverErrors = getFieldErrors(err);
      if (serverErrors) {
        const fieldErrors: Record<string, string> = {};
        const nonFieldErrors: Record<string, string> = {};
        for (const [key, value] of Object.entries(serverErrors as Record<string, string>)) {
          if (fieldNames.has(key)) {
            fieldErrors[key] = value;
          } else {
            nonFieldErrors[key] = value;
          }
        }
        serverErrorFieldSlice.setValue(fieldErrors);
        if (Object.keys(nonFieldErrors).length > 0) {
          serverErrorSlice.setValue(errorToString(nonFieldErrors));
        }
      } else {
        serverErrorSlice.setValue(errorToString(err));
      }
    }
  );
};

const checkIfPresent = <T extends any>(
  getDataFn: () => T,
  api: any
  // debug?: boolean
): {isLoaded: boolean; result: T} => {
  let isLoaded = true;
  const unsub = api.registerOnNotLoadedListener((status: string) => {
    if (status === "loading" || status === "notLoaded") {
      // if (debug) debugger;
      isLoaded = false;
    }
  });
  const result = getDataFn();
  unsub();
  return {isLoaded, result};
};

export const useNextFormWithLoadingData = <TSchema extends v.BaseSchema<any, any, any>>(
  args: Omit<ValibotFormArgs<TSchema>, "initial"> & {
    loadInitialValues: () => v.InferInput<TSchema>;
    api: any;
  }
) => {
  const {onSubmit, loadInitialValues, api, ...rest} = args;
  const trigger = useDelayedTrigger();

  const handleSubmit = (values: v.InferOutput<TSchema>) => {
    const res = args.onSubmit(values);
    if (res && "then" in res) {
      const fieldNames = new Set(Object.keys(values as Record<string, unknown>));
      handleSubmitPromise(res, {
        trigger,
        formStateSlice,
        serverErrorSlice,
        serverErrorFieldSlice,
        fieldNames,
      });
    }
  };

  const [initialValues, setInitialValues] = useState(() => checkIfPresent(loadInitialValues, api));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialValues.isLoaded) return;
    const res = checkIfPresent(loadInitialValues, api);
    if (res.isLoaded) {
      setInitialValues(res);
      neoFormProps.reset(res.result);
    }
  });

  const {
    onSubmit: _formSubmit,
    createCustomDataSlice,
    ...neoFormProps
  } = useValibotNeoForm({
    ...rest,
    onSubmit: handleSubmit,
    shouldFocusError: true,
    initial: initialValues.result,
  });
  const formStateSlice = createCustomDataSlice<FormSubmitState>(
    "formState",
    initialValues.isLoaded ? "initial" : "loading"
  );
  const serverErrorSlice = createCustomDataSlice<string | null>("serverError", null);
  const serverErrorFieldSlice = createCustomDataSlice<Record<string, string>>(
    "serverFieldError",
    {}
  );

  const Form = useBuildFormComponent(FormWrapper, {
    onSubmit: _formSubmit,
    formStateSlice,
    serverErrorSlice,
  });

  return {
    ...neoFormProps,
    formStateSlice,
    serverErrorSlice,
    Form,
    triggerSubmit: () => _formSubmit(),
    useButtonProps: () =>
      ({
        state: formStateSlice.useValue((s) => s),
        type: "submit",
      }) as const,
  };
};

export const useNextForm = <TSchema extends v.BaseSchema<any, any, any>>(
  args: ValibotFormArgs<TSchema>
) => {
  const {onSubmit, ...rest} = args;
  const trigger = useDelayedTrigger();

  const handleSubmit = (values: v.InferOutput<TSchema>) => {
    const res = args.onSubmit(values);
    if (res && "then" in res) {
      const fieldNames = new Set(Object.keys(values as Record<string, unknown>));
      handleSubmitPromise(res, {
        trigger,
        formStateSlice,
        serverErrorSlice,
        serverErrorFieldSlice,
        fieldNames,
      });
    }
  };

  const {
    onSubmit: _formSubmit,
    createCustomDataSlice,
    ...neoFormProps
  } = useValibotNeoForm({
    ...rest,
    onSubmit: handleSubmit,
    shouldFocusError: true,
  });
  const formStateSlice = createCustomDataSlice<FormSubmitState>("formState", "initial");
  const serverErrorSlice = createCustomDataSlice<string | null>("serverError", null);
  const serverErrorFieldSlice = createCustomDataSlice<Record<string, string>>(
    "serverFieldError",
    {}
  );

  const Form = useBuildFormComponent(FormWrapper, {
    onSubmit: _formSubmit,
    formStateSlice,
    serverErrorSlice,
  });

  return {
    ...neoFormProps,
    formStateSlice,
    serverErrorSlice,
    Form,
    triggerSubmit: () => _formSubmit(),
    useButtonProps: () =>
      ({
        state: formStateSlice.useValue((s) => s),
        type: "submit",
      }) as const,
  };
};
